// Venta administrativa: una venta cargada desde Facturacion por un
// administrador que NO pasa por el mostrador.
//
// Reglas (spec: desarrollo/spec-venta-administrativa-sin-caja.md):
//  - Cliente con nombre: va SIEMPRE a cuenta corriente (client_movements +
//    sales_receivables). El cobro posterior entra por Cobranzas.
//  - Consumidor Final: se elige en cada venta entre
//      * 'sin_movimiento': factura/venta sin registrar plata (ya se cobro o se
//        registro por otro lado);
//      * 'banco': cobrada por un medio con banco -> movimientos_financieros y
//        saldo del banco, igual que el POS, pero sin caja.
//  - NUNCA inserta movimientos_caja: no aparece en el arqueo ni en el teorico.
//  - Stock opcional (`afectaStock`). Si se descuenta, deja kardex igual que el
//    POS; si no, no toca stock_levels ni movimientos_stock.
//  - Deja la venta con invoice_status 'pendiente': aparece en Facturacion,
//    IVA, rentabilidad, CRM y reportes como cualquier otra.
//
// Nota de orden: la venta se inserta con status 'pending' y se pasa a
// 'completed' al final. En Impulza hay un trigger AFTER INSERT
// (fn_sales_to_client_movement) que genera la deuda si la venta nace
// 'completed'; en Gastify ese trigger no existe. Naciendo 'pending' el trigger
// no actua en ninguna de las dos bases y la deuda la genera siempre
// syncSaleClientDebit: mismo comportamiento en ambas, y si algo falla antes de
// completar, se puede deshacer sin haber tocado la cuenta corriente.
//
// `supabase` y las funciones de cuenta corriente se cargan con import()
// dinamico (mismo motivo que presupuestoConfirmacion.js: ../supabase lee
// import.meta.env y no carga bajo node --test).
import { buildSalePaymentTerms } from './paymentTerms.js';
import { isGenericConsumidorFinalClient } from './consumidorFinal.js';
import { getLocalDateKey, buildLocalOperationTimestamp } from './localDateTime.js';
import { addSettlementDays } from './paymentSettlement.js';

export const VENTA_ADMINISTRATIVA_ORIGEN = 'administrativa';
export const VENTA_ADMINISTRATIVA_MODULE_ID = 'facturacion-venta-administrativa';
export const MOTIVO_MIN_LENGTH = 10;

/** Como se cobra la venta administrativa. */
export const COBRO = Object.freeze({
  CUENTA_CORRIENTE: 'cuenta_corriente',
  SIN_MOVIMIENTO: 'sin_movimiento',
  BANCO: 'banco',
});

/** Sin cliente o con el cliente generico "Consumidor Final". */
export const esVentaAConsumidorFinal = (cliente) =>
  !cliente?.id || isGenericConsumidorFinalClient(cliente);

/** Cobro efectivo: con cliente con nombre siempre es cuenta corriente. */
export const resolverCobro = (cliente, cobro) =>
  (esVentaAConsumidorFinal(cliente) ? cobro || null : COBRO.CUENTA_CORRIENTE);
// Cuantos dias hacia atras se admite la fecha de la venta. Alineado con la
// ventana que ARCA acepta para la fecha de un comprobante de productos.
export const MAX_DIAS_ATRAS = 5;

const PRIVILEGED_ROLES = new Set(['admin', 'administrador']);

async function loadDefaultSupabaseClient() {
  const mod = await import('../supabase.js');
  return mod.supabase;
}

async function loadDefaultDeps() {
  const mod = await import('./salesReceivables.js');
  return {
    syncSaleClientDebit: mod.syncSaleClientDebit,
    syncSaleReceivable: mod.syncSaleReceivable,
  };
}

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const shiftDateKey = (dateKey, days) => {
  const [y, m, d] = String(dateKey || '').split('-').map(Number);
  if (!y || !m || !d) return '';
  return getLocalDateKey(new Date(y, m - 1, d + (Number(days) || 0)));
};

/**
 * Permiso: admin/administrador siempre; el resto necesita el modulo
 * `facturacion-venta-administrativa` (o 'all'). Falla cerrado sin perfil.
 */
export function canCreateVentaAdministrativa({ profile = null, isSuperAdmin = false, effectiveModules = null } = {}) {
  if (isSuperAdmin) return true;
  if (!profile) return false;
  const role = String(profile.role || '').trim().toLowerCase();
  if (PRIVILEGED_ROLES.has(role)) return true;
  const modules = effectiveModules instanceof Set
    ? effectiveModules
    : new Set(Array.isArray(effectiveModules) ? effectiveModules : []);
  return modules.has('all') || modules.has(VENTA_ADMINISTRATIVA_MODULE_ID);
}

export function calcularTotalVentaAdministrativa(items = []) {
  return round2(
    (Array.isArray(items) ? items : []).reduce(
      (acc, it) => acc + (Number(it?.quantity) || 0) * (Number(it?.unit_price) || 0),
      0,
    ),
  );
}

/**
 * Validacion pura. Devuelve la lista de errores (vacia = ok).
 * `hoy` es la fecha local 'YYYY-MM-DD' (inyectable para tests).
 */
export function validarVentaAdministrativa({ cliente, siteId, items, motivo, fecha, hoy, cobro, metodoBanco } = {}) {
  const errores = [];
  const today = hoy || getLocalDateKey();

  if (esVentaAConsumidorFinal(cliente)) {
    if (cobro === COBRO.CUENTA_CORRIENTE) {
      errores.push('Consumidor Final no tiene cuenta corriente. Elegí un cliente con nombre.');
    } else if (cobro !== COBRO.SIN_MOVIMIENTO && cobro !== COBRO.BANCO) {
      errores.push('Elegí cómo se cobró: sin movimiento de plata o cobrada por banco.');
    } else if (cobro === COBRO.BANCO && !metodoBanco?.bank_id) {
      errores.push('Elegí el medio de pago con banco.');
    }
  }

  if (!siteId) errores.push('Elegí la sucursal.');

  const lista = Array.isArray(items) ? items : [];
  if (lista.length === 0) errores.push('Agregá al menos un producto.');
  lista.forEach((it, idx) => {
    const n = idx + 1;
    if (it?.is_misc) {
      // "Productos Varios": sin producto del catalogo, importe libre (igual
      // que el item manual del POS). No mueve stock.
      if (!String(it?.name || '').trim()) errores.push(`Ítem ${n}: poné una descripción.`);
      if (!(Number(it?.unit_price) > 0)) errores.push(`Ítem ${n}: poné el importe.`);
    } else if (!it?.product_id) {
      errores.push(`Ítem ${n}: producto inválido.`);
    }
    if (!(Number(it?.quantity) > 0)) errores.push(`Ítem ${n}: la cantidad tiene que ser mayor a 0.`);
    if (!(Number(it?.unit_price) >= 0)) errores.push(`Ítem ${n}: precio inválido.`);
    // Los combos disparan en la base un descuento automatico de componentes
    // (trigger trg_deduct_combo_stock) que ignora el toggle de stock.
    if (String(it?.product_type || '') === 'Combo') {
      errores.push(`Ítem ${n}: los combos no se pueden cargar como venta administrativa.`);
    }
  });

  if (lista.length > 0 && !(calcularTotalVentaAdministrativa(lista) > 0)) {
    errores.push('El total tiene que ser mayor a 0.');
  }

  if (String(motivo || '').trim().length < MOTIVO_MIN_LENGTH) {
    errores.push(`Escribí el motivo (mínimo ${MOTIVO_MIN_LENGTH} caracteres).`);
  }

  if (fecha) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(fecha))) {
      errores.push('Fecha inválida.');
    } else if (fecha > today) {
      errores.push('La fecha no puede ser futura.');
    } else if (fecha < shiftDateKey(today, -MAX_DIAS_ATRAS)) {
      errores.push(`La fecha no puede ser anterior a ${MAX_DIAS_ATRAS} días.`);
    }
  }

  return errores;
}

/**
 * Arma las filas de sales y sale_items. Pura: no toca la red.
 */
export function buildVentaAdministrativaRows({
  organizationId,
  userId,
  cliente,
  siteId,
  items,
  afectaStock,
  fecha,
  createdAt,
  billingEntityId = null,
  ctaCteMethod = null,
  cobro = null,
  metodoBanco = null,
}) {
  const total = calcularTotalVentaAdministrativa(items);
  const fechaVenta = fecha || getLocalDateKey();
  const cobroEfectivo = resolverCobro(cliente, cobro);
  const esCtaCte = cobroEfectivo === COBRO.CUENTA_CORRIENTE;
  const terms = esCtaCte
    ? buildSalePaymentTerms({ createdAt, sale: {}, client: cliente })
    : { days: 0, label: 'Contado' };
  // Vencimiento sobre el dia LOCAL de la venta (no sobre el ISO en UTC, que
  // de noche cae al dia siguiente).
  const dueDate = shiftDateKey(fechaVenta, terms.days) || fechaVenta;

  let paymentMethod = { name: ctaCteMethod?.name || 'Cuenta Corriente', id: ctaCteMethod?.id || null };
  if (cobroEfectivo === COBRO.BANCO) paymentMethod = { name: metodoBanco?.name || 'Banco', id: metodoBanco?.id || null };
  if (cobroEfectivo === COBRO.SIN_MOVIMIENTO) paymentMethod = { name: 'Sin movimiento de plata', id: null };

  const sale = {
    organization_id: organizationId,
    client_id: esVentaAConsumidorFinal(cliente) ? (cliente?.id || null) : cliente.id,
    site_id: siteId,
    total,
    payment_method: paymentMethod.name,
    payment_method_id: paymentMethod.id,
    status: 'pending',
    invoice_status: 'pendiente',
    active: true,
    user_id: userId || null,
    created_at: createdAt,
    fecha_venta: fechaVenta,
    billing_entity_id: billingEntityId || null,
    billing_source: billingEntityId ? 'default' : null,
    invoice_sent_at: null,
    invoice_cbte_tipo: null,
    payment_condition_days: terms.days,
    payment_condition_label: terms.label,
    payment_due_date: dueDate,
    afecta_caja: false,
    afecta_stock: Boolean(afectaStock),
    origen: VENTA_ADMINISTRATIVA_ORIGEN,
  };

  const saleItems = (items || []).map((it) => {
    const quantity = Number(it.quantity) || 0;
    const unitPrice = round2(it.unit_price);
    const iva = Number(it.iva_percentage);
    return {
      organization_id: organizationId,
      product_id: it.is_misc ? null : it.product_id,
      variant_id: it.is_misc ? null : (it.variant_id || null),
      product_name_snapshot: String(it.name || it.product_name_snapshot || '').trim() || (it.is_misc ? 'Productos Varios' : 'Producto'),
      quantity,
      unit_price: unitPrice,
      subtotal: round2(quantity * unitPrice),
      iva_percentage: Number.isNaN(iva) ? 21 : iva,
    };
  });

  return { sale, saleItems, total, terms: { ...terms, dueDate }, cobro: cobroEfectivo };
}

// --- stock con snapshot para poder deshacer ------------------------------- //

async function descontarStock({ client, organizationId, siteId, saleItems, saleShortId, nowIso, allowNegativeStock = true }) {
  const snapshots = [];
  const movementIds = [];
  const quantitiesByProduct = new Map();
  for (const item of saleItems) {
    if (!item.product_id) continue;
    quantitiesByProduct.set(
      item.product_id,
      (quantitiesByProduct.get(item.product_id) || 0) + (Number(item.quantity) || 0),
    );
  }

  // Lectura previa de todo el stock: si la org no admite negativo, se corta
  // ANTES de escribir nada (no hay nada que revertir).
  const previousByProduct = new Map();
  for (const [productId, quantity] of quantitiesByProduct) {
    const { data: row, error: readError } = await client
      .from('stock_levels')
      .select('id, quantity')
      .eq('product_id', productId)
      .eq('site_id', siteId)
      .maybeSingle();
    if (readError && readError.code !== 'PGRST116') throw readError;
    const previous = row?.id ? Number(row.quantity) || 0 : null;
    if (!allowNegativeStock && (previous || 0) - quantity < 0) {
      const item = saleItems.find((it) => it.product_id === productId);
      throw new Error(`Sin stock suficiente en la sucursal: ${item?.product_name_snapshot || 'producto'} (hay ${previous || 0}).`);
    }
    previousByProduct.set(productId, previous);
  }

  try {
    for (const [productId, quantity] of quantitiesByProduct) {
      const previous = previousByProduct.get(productId);
      const { error: upsertError } = await client.from('stock_levels').upsert(
        {
          organization_id: organizationId,
          product_id: productId,
          site_id: siteId,
          quantity: (previous || 0) - quantity,
          updated_at: nowIso,
        },
        { onConflict: 'product_id,site_id' },
      );
      if (upsertError) throw upsertError;
      snapshots.push({ productId, previous });
    }

    const movementRows = saleItems
      .filter((item) => item.product_id)
      .map((item) => ({
        organization_id: organizationId,
        producto_id: item.product_id,
        tipo: 'Egreso',
        cantidad: Number(item.quantity) || 0,
        // Mismo prefijo "Venta POS #XXXXXXXX" que usan los cruces del kardex.
        motivo: `Venta POS #${saleShortId} (Venta administrativa).`,
        sitio_origen: siteId,
        sitio_destino: null,
      }));

    if (movementRows.length > 0) {
      const { data: inserted, error: movementError } = await client
        .from('movimientos_stock')
        .insert(movementRows)
        .select('id');
      if (movementError) throw movementError;
      (inserted || []).forEach((m) => m?.id && movementIds.push(m.id));
    }
  } catch (error) {
    await revertirStock({ client, siteId, snapshots, movementIds, nowIso });
    throw error;
  }

  return { snapshots, movementIds };
}

// supabase-js no lanza: devuelve { error }. Se convierte en excepcion para
// que un rollback fallido no pase por exitoso.
const ensureOk = (result) => {
  if (result?.error) throw result.error;
  return result;
};

/** @returns {Promise<boolean>} true si el stock quedo como estaba */
async function revertirStock({ client, siteId, snapshots = [], movementIds = [], nowIso }) {
  try {
    if (movementIds.length > 0) {
      ensureOk(await client.from('movimientos_stock').delete().in('id', movementIds));
    }
    for (const snap of [...snapshots].reverse()) {
      if (snap.previous === null) {
        ensureOk(await client.from('stock_levels').delete()
          .eq('product_id', snap.productId).eq('site_id', siteId));
      } else {
        ensureOk(await client.from('stock_levels')
          .update({ quantity: snap.previous, updated_at: nowIso })
          .eq('product_id', snap.productId).eq('site_id', siteId));
      }
    }
    return true;
  } catch (rollbackError) {
    console.error('Venta administrativa: no se pudo revertir el stock', rollbackError);
    return false;
  }
}

/**
 * Borra la venta que no llego a completarse. Si el borrado falla, la anula
 * (active=false) para que no aparezca en Facturacion como pendiente de emitir.
 * @returns {Promise<boolean>} true si la venta ya no queda activa
 */
async function borrarVentaIncompleta(client, saleId) {
  try {
    ensureOk(await client.from('sale_items').delete().eq('sale_id', saleId));
    ensureOk(await client.from('sales').delete().eq('id', saleId));
    return true;
  } catch (cleanupError) {
    console.error('Venta administrativa: no se pudo borrar la venta incompleta', cleanupError);
    try {
      ensureOk(await client.from('sales')
        .update({ active: false, status: 'anulada', invoice_status: 'anulada' })
        .eq('id', saleId));
      return true;
    } catch (annulError) {
      console.error('Venta administrativa: tampoco se pudo anular la venta incompleta', annulError);
      return false;
    }
  }
}

/** Productos reales de los items: el tipo sale de la base, no del caller. */
async function cargarTiposDeProducto(client, organizationId, items) {
  const ids = Array.from(new Set((items || []).filter((it) => !it.is_misc).map((it) => it.product_id).filter(Boolean)));
  if (ids.length === 0) return new Map();
  const { data, error } = await client
    .from('products')
    .select('id, name, product_type')
    .eq('organization_id', organizationId)
    .in('id', ids);
  if (error) throw error;
  return new Map((data || []).map((p) => [p.id, p]));
}

/**
 * Registra la deuda y la cuenta por cobrar de una venta administrativa ya
 * completa. Idempotente (syncSaleClientDebit / syncSaleReceivable comparan con
 * lo ya registrado), asi que sirve para reintentar si fallo la primera vez.
 */
export async function registrarCuentaCorrienteVentaAdministrativa({
  saleId,
  organizationId,
  cliente,
  siteId,
  total,
  motivo,
  terms,
  deps,
}) {
  const { syncSaleClientDebit, syncSaleReceivable } = deps || (await loadDefaultDeps());
  const saleShortId = String(saleId).slice(0, 8).toUpperCase();

  await syncSaleClientDebit({
    saleId,
    organizationId,
    clientId: cliente.id,
    totalAmount: total,
    movementType: 'Venta POS',
    observations: `Venta administrativa #${saleShortId}. ${String(motivo || '').trim()}`.trim(),
    shouldApply: true,
  });

  await syncSaleReceivable({
    saleId,
    organizationId,
    clientId: cliente.id,
    siteId,
    source: 'pos',
    description: `Venta administrativa #${saleShortId} - ${cliente.full_name || 'Cliente'} (${terms.label})`,
    dueDate: terms.dueDate,
    totalAmount: total,
    active: true,
  });
}

/**
 * Registra el ingreso bancario de una venta administrativa a Consumidor Final
 * cobrada por banco: misma logica que el POS / presupuestoConfirmacion
 * (comision, dias de acreditacion, saldo del banco si acredita en el dia),
 * pero sin caja. Idempotente: si ya hay movimiento financiero de la venta, no
 * repite nada.
 */
export async function registrarCobroBancarioVentaAdministrativa({
  saleId,
  organizationId,
  siteId,
  total,
  metodoBanco,
  userId = null,
  createdAt,
  supabaseClient,
}) {
  const client = supabaseClient || (await loadDefaultSupabaseClient());
  const saleShortId = String(saleId).slice(0, 8).toUpperCase();

  const { data: existentes, error: existError } = await client
    .from('movimientos_financieros')
    .select('id')
    .eq('organization_id', organizationId)
    .eq('documento_tipo', 'Venta')
    .eq('documento_id', saleId);
  if (existError) throw existError;
  if ((existentes || []).length > 0) return { skipped: true, reason: 'ya_registrado' };

  const nowIso = createdAt || new Date().toISOString();
  const commissionPercentage = Math.max(0, Number(metodoBanco?.commission_percentage) || 0);
  const accreditationDays = Math.max(0, Number(metodoBanco?.accreditation_days) || 0);
  const businessDaysOnly = Boolean(metodoBanco?.accreditation_business_days);
  const commissionAmount = round2(total * (commissionPercentage / 100));
  const bankNetAmount = Math.max(0, round2(total - commissionAmount));
  const settlementDate = addSettlementDays(nowIso, accreditationDays, businessDaysOnly);
  const settlesImmediately = accreditationDays === 0;

  let saldoResultante = null;
  if (settlesImmediately) {
    const { data: banco, error: bancoError } = await client
      .from('banks')
      .select('id, current_balance')
      .eq('id', metodoBanco.bank_id)
      .single();
    if (bancoError) throw bancoError;
    saldoResultante = round2((Number(banco?.current_balance) || 0) + bankNetAmount);
  }

  const descripcion = `Venta administrativa #${saleShortId}. Consumidor Final. Bruto: ${total}. Comision ${commissionPercentage}%: ${commissionAmount}. Neto: ${bankNetAmount}. Acreditacion: ${accreditationDays} dia(s)${businessDaysOnly ? ' habiles' : ''}.`;

  // Primero el movimiento (es el que marca la idempotencia), despues el saldo.
  const { error: mfError } = await client.from('movimientos_financieros').insert([
    {
      organization_id: organizationId,
      bank_id: metodoBanco.bank_id,
      sitio_id: siteId,
      tipo: 'Ingreso',
      categoria: 'Venta',
      monto: bankNetAmount,
      saldo_resultante: saldoResultante,
      entidad_tipo: 'Cliente',
      entidad_id: null,
      documento_tipo: 'Venta',
      documento_id: saleId,
      descripcion,
      fecha_operacion: settlementDate,
      user_id: userId,
    },
  ]);
  if (mfError) throw mfError;

  if (settlesImmediately) {
    const { error: bankUpdateError } = await client
      .from('banks')
      .update({ current_balance: saldoResultante })
      .eq('id', metodoBanco.bank_id);
    if (bankUpdateError) throw bankUpdateError;
  }

  return { applied: true, bankNetAmount };
}

/**
 * Completa la parte de plata de una venta ya creada (reintento desde el modal).
 * `args.cobro` decide: cuenta corriente o banco.
 */
export async function registrarCobroVentaAdministrativa(args) {
  if (args?.cobro === COBRO.BANCO) return registrarCobroBancarioVentaAdministrativa(args);
  return registrarCuentaCorrienteVentaAdministrativa(args);
}

/**
 * Crea la venta administrativa con todos sus efectos.
 * @returns {Promise<{ ok: true, saleId: string, total: number }>}
 */
export async function crearVentaAdministrativa({
  organizationId,
  userId,
  profile,
  isSuperAdmin = false,
  effectiveModules = null,
  cliente,
  siteId,
  items,
  afectaStock = true,
  fecha,
  motivo,
  billingEntityId = null,
  ctaCteMethod = null,
  cobro = null,
  metodoBanco = null,
  allowNegativeStock = true,
  ahora,
  hoy,
  supabaseClient,
  deps,
}) {
  // Guarda en el servicio, no solo en la UI: un render viejo no crea ventas.
  if (!canCreateVentaAdministrativa({ profile, isSuperAdmin, effectiveModules })) {
    throw new Error('No tenés permiso para cargar ventas administrativas.');
  }

  // `hoy` es inyectable para tests; en producción queda undefined y vale el día local real.
  const errores = validarVentaAdministrativa({ cliente, siteId, items, motivo, fecha, hoy, cobro, metodoBanco });
  if (errores.length > 0) throw new Error(errores[0]);

  const client = supabaseClient || (await loadDefaultSupabaseClient());
  const resolvedDeps = deps || (await loadDefaultDeps());

  // Los combos disparan en la base un descuento de componentes que ignora el
  // toggle de stock (trg_deduct_combo_stock). Se valida contra `products`, no
  // contra lo que mando la pantalla.
  const productos = await cargarTiposDeProducto(client, organizationId, items);
  for (const it of items) {
    if (it.is_misc) continue;
    const prod = productos.get(it.product_id);
    if (!prod) throw new Error('Hay un producto que no existe en esta organización.');
    if (prod.product_type === 'Combo') {
      throw new Error(`${prod.name}: los combos no se pueden cargar como venta administrativa.`);
    }
  }

  const createdAt = ahora || buildLocalOperationTimestamp(fecha || getLocalDateKey());
  const { sale, saleItems, total, terms, cobro: cobroEfectivo } = buildVentaAdministrativaRows({
    organizationId,
    userId,
    cliente,
    siteId,
    items,
    afectaStock,
    fecha,
    createdAt,
    billingEntityId,
    ctaCteMethod,
    cobro,
    metodoBanco,
  });

  const { data: inserted, error: saleError } = await client.from('sales').insert([sale]).select().single();
  if (saleError) throw saleError;
  const saleId = inserted.id;
  const saleShortId = String(saleId).slice(0, 8).toUpperCase();

  let stockResult = null;
  try {
    const { error: itemsError } = await client
      .from('sale_items')
      .insert(saleItems.map((row) => ({ ...row, sale_id: saleId })));
    if (itemsError) throw itemsError;

    if (afectaStock) {
      stockResult = await descontarStock({
        client, organizationId, siteId, saleItems, saleShortId, nowIso: createdAt, allowNegativeStock,
      });
    }

    const { error: completeError } = await client
      .from('sales')
      .update({ status: 'completed' })
      .eq('id', saleId);
    if (completeError) throw completeError;
  } catch (error) {
    const stockOk = stockResult
      ? await revertirStock({ client, siteId, ...stockResult, nowIso: createdAt })
      : true;
    const ventaOk = await borrarVentaIncompleta(client, saleId);
    if (!stockOk || !ventaOk) {
      const detalle = [
        !ventaOk ? `la venta #${saleShortId} quedó creada sin completar` : null,
        !stockOk ? 'el stock quedó descontado' : null,
      ].filter(Boolean).join(' y ');
      const err = new Error(`${error?.message || 'Error al guardar'}. Además, ${detalle}: revisalo antes de reintentar.`);
      err.cause = error;
      throw err;
    }
    throw error;
  }

  // Desde aca la venta existe y esta completa. Si falla la parte de plata
  // (cuenta corriente o banco), NO se borra la venta (ya desconto stock y la
  // auditoria la registro): el error trae lo necesario para reintentar con
  // registrarCobroVentaAdministrativa (el modal ofrece el boton).
  if (cobroEfectivo !== COBRO.SIN_MOVIMIENTO) {
    const retryArgs = cobroEfectivo === COBRO.BANCO
      ? { cobro: COBRO.BANCO, saleId, organizationId, siteId, total, metodoBanco, userId: userId || null, createdAt }
      : { cobro: COBRO.CUENTA_CORRIENTE, saleId, organizationId, cliente, siteId, total, motivo, terms };
    try {
      if (cobroEfectivo === COBRO.BANCO) {
        await registrarCobroBancarioVentaAdministrativa({ ...retryArgs, supabaseClient: client });
      } else {
        await registrarCuentaCorrienteVentaAdministrativa({ ...retryArgs, deps: resolvedDeps });
      }
    } catch (cobroError) {
      const destino = cobroEfectivo === COBRO.BANCO ? 'el banco' : 'la cuenta corriente';
      const err = new Error(
        `La venta #${saleShortId} se creó, pero no se pudo registrar en ${destino}: ${cobroError?.message || cobroError}`,
      );
      err.saleId = saleId;
      err.partial = true;
      err.retryArgs = retryArgs;
      throw err;
    }
  }

  // Auditoria: motivo y flags. No bloquea si falla.
  try {
    await client.from('status_history').insert({
      organization_id: organizationId,
      entity_type: 'sales',
      entity_id: saleId,
      field_name: 'origen',
      from_value: null,
      to_value: VENTA_ADMINISTRATIVA_ORIGEN,
      changed_by: userId || null,
      metadata: {
        motivo: String(motivo).trim(),
        afecta_caja: false,
        afecta_stock: Boolean(afectaStock),
        cobro: cobroEfectivo,
        total,
      },
    });
  } catch (historyError) {
    console.warn('Venta administrativa: no se pudo registrar el historial', historyError);
  }

  return { ok: true, saleId, total };
}
